import React, { useState } from 'react';
import { Grid } from '@mui/material';
import { todoIcon } from '@europrocurement/l2d-icons';
import { FormaliteJsonldFormaliteUpdate } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import {
    UPDATE_ITEM_CTX,
    useFormalitiesCtx,
    useFormalitiesCtxDispatch,
} from '@europrocurement/l2d-modules';
import FormalityCompatibilitiesCard from './FormalityCompatibilitiesCard';
import CardTitle from '../../components/CardTitle';
import ActionButton from '../../components/ActionButton';
import CompatibilitiesModal from './CompatibilitiesModal';
import useFormalityFormHandler from '../../hooks/useFormalityFormHandler';
import useCompatibility from '../../hooks/useCompatibility';

type FormalityCompatbilitiesTabProps = {
    formalityId: number;
};

const FormalityCompatbilitiesTab: React.FunctionComponent<FormalityCompatbilitiesTabProps> =
    function (props) {
        const { formalityId } = props;
        const { getFormalityById } = useFormalitiesCtx();
        const formality = getFormalityById(formalityId);
        const formalityDispatch = useFormalitiesCtxDispatch();
        const [editModalOpen, setEditModalOpen] = useState(false);
        const { updateFormality } = useFormalityFormHandler();
        const { toggleCompatibility } = useCompatibility();

        if (!formality) {
            return null;
        }

        const onOpenCompatibilitiesModal = () => {
            setEditModalOpen(true);
        };
        const onCloseCompatibilitiesModal = () => {
            setEditModalOpen(false);
        };
        const onEditCompatibilities = (selectedItems: (string | number)[]) => {
            updateFormality(formality, {
                compatibilities: selectedItems,
            } as FormaliteJsonldFormaliteUpdate).then(() => {
                setEditModalOpen(false);

                if (formalityDispatch) {
                    formalityDispatch({
                        type: UPDATE_ITEM_CTX,
                        payload: {
                            ...formality,
                            compatibilites: selectedItems as number[],
                        },
                    });

                    selectedItems.forEach((itemId) => {
                        const childFormality = getFormalityById(itemId as number);

                        if (childFormality) {
                            const childCombinabilities = childFormality.compatibilites.filter(
                                (id) => id !== formality.id,
                            );
                            formalityDispatch({
                                type: UPDATE_ITEM_CTX,
                                payload: {
                                    ...childFormality,
                                    compatibilites: [...childCombinabilities, formality.id],
                                },
                            });
                        }
                    });
                }
            });
        };
        const onRemoveCompatibility = (compatibilityId: string | number) => {
            const childFormality = getFormalityById(compatibilityId as number);
            if (childFormality) {
                toggleCompatibility(formality, childFormality);
            }
        };

        return (
            <>
                <Grid container>
                    <Grid
                        item
                        xs={12}
                        md={10}
                    >
                        <CardTitle title="Liste des formalités compatibles" />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={2}
                    >
                        <ActionButton
                            label="Modifier la liste"
                            onClick={onOpenCompatibilitiesModal}
                            icon={todoIcon}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                    >
                        <FormalityCompatibilitiesCard
                            formalityId={formality.id as number}
                            onRemoveCompatibility={onRemoveCompatibility}
                        />
                    </Grid>
                </Grid>
                <CompatibilitiesModal
                    isOpen={editModalOpen}
                    onModalClose={onCloseCompatibilitiesModal}
                    onSubmitModal={onEditCompatibilities}
                    itemsSelected={formality?.compatibilites}
                    formality={formality}
                />
            </>
        );
    };

export default FormalityCompatbilitiesTab;
